import React, { useState, useEffect, useRef } from "react";
import { useDragAndDrop } from "@formkit/drag-and-drop/react";
import { Dialog } from '@headlessui/react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { dataService } from "../../../../services/dataService";
import config from "../../../../config";

export default function ReorderLotsModal({ 
    isOpen, 
    onClose, 
    auctionData, 
    onReorderSuccess,
    onReorderError 
}) {
    const [reorderedLots, setReorderedLots] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingLots, setIsLoadingLots] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [listRef, dndLots, setDndLots] = useDragAndDrop([], {});
    const dragSnapshotRef = useRef({
        startItems: null,
        startPoint: null,
        didMove: false,
        active: false,
    });

    // Fetch lots when modal opens
    useEffect(() => {
        if (isOpen) {
            // Immediately reset local lists to avoid showing stale order before fresh fetch completes
            setReorderedLots([]);
            setDndLots([]);
            setHasChanges(false);
            dragSnapshotRef.current = { startItems: null, startPoint: null, didMove: false, active: false };
            fetchLots();
        }
    }, [isOpen, auctionData?.auction_id]);

    const fetchLots = async () => {
        try {
            if (!auctionData?.auction_id) return;
            setIsLoadingLots(true);
            const endpoint = `?auction_id=${auctionData.auction_id}&per_page=5000`;
            const response = await dataService.LotList(endpoint);
            if (response.error === false) {
                const fetchedLots = response.data?.data || [];
                const initialLots = [...fetchedLots].sort((a, b) => a.lot_number - b.lot_number);
                setReorderedLots(initialLots);
                setDndLots(initialLots);
                setHasChanges(false);
            }
        } catch (e) {
            console.error('Failed to fetch lots for reorder modal:', e);
        } finally {
            setIsLoadingLots(false);
        }
    }

    // Mirror DnD list to component state and set change flag
    useEffect(() => {
        if (!Array.isArray(dndLots)) return;
        setReorderedLots(dndLots);
        const changed = dndLots.some((lot, index) => lot?.lot_number !== (index + 1));
        setHasChanges(changed);
    }, [dndLots]);

    // drag cancel / safe drop helpers
    const DRAG_THRESHOLD_PX = 8;
    const resetItemZIndex = () => {
        const listEl = listRef.current;
        if (!listEl) return;
        const elements = listEl.querySelectorAll('li, li > div');
        elements.forEach((el) => {
            if (el && el.style && el.style.zIndex) {
                el.style.zIndex = '';
            }
        });
    };
    const onGlobalPointerMove = (e) => {
        const snap = dragSnapshotRef.current;
        if (!snap.active || !snap.startPoint) return;
        const dx = (e.clientX ?? 0) - snap.startPoint.x;
        const dy = (e.clientY ?? 0) - snap.startPoint.y;
        if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
            snap.didMove = true;
        }
    };
    const onGlobalPointerUp = (e) => {
        const snap = dragSnapshotRef.current;
        if (!snap.active) return;
        try {
            const x = e.clientX ?? 0;
            const y = e.clientY ?? 0;
            const listEl = listRef.current;
            let releasedInsideList = false;
            if (listEl) {
                const r = listEl.getBoundingClientRect();
                releasedInsideList = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
            }
            const shouldRevert = !snap.didMove || !releasedInsideList;
            if (shouldRevert && Array.isArray(snap.startItems)) {
                setDndLots(snap.startItems);
            }
        } finally {
            dragSnapshotRef.current = { startItems: null, startPoint: null, didMove: false, active: false };
            window.removeEventListener('pointermove', onGlobalPointerMove);
            window.removeEventListener('pointerup', onGlobalPointerUp, true);
            // ensure any elevated z-index on items are cleared after drag
            resetItemZIndex();
        }
    };
    const handleItemPointerDown = (e) => {
        dragSnapshotRef.current = {
            startItems: [...reorderedLots],
            startPoint: { x: e.clientX ?? 0, y: e.clientY ?? 0 },
            didMove: false,
            active: true,
        };
        window.addEventListener('pointermove', onGlobalPointerMove);
        // capture to ensure we get it even if DnD stops propagation
        window.addEventListener('pointerup', onGlobalPointerUp, true);
    };
    useEffect(() => {
        return () => {
            window.removeEventListener('pointermove', onGlobalPointerMove);
            window.removeEventListener('pointerup', onGlobalPointerUp, true);
        };
    }, []);

    // When any dialog opens, ensure list items don't overlay it
    useEffect(() => {
        if (showSaveDialog || showDiscardDialog) {
            resetItemZIndex();
        }
    }, [showSaveDialog, showDiscardDialog]);

    const handleSave = async () => {
        if (!hasChanges) {
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            // Check if auction is still in draft state
            if (auctionData.status !== 'Draft') {
                onReorderError('Auction is no longer in draft state. Cannot reorder lots.');
                setIsLoading(false);
                return;
            }

            // Find lots that actually changed position
            const changedLots = reorderedLots.filter((lot, index) => 
                lot.lot_number !== (index + 1)
            );

            if (changedLots.length === 0) {
                onClose();
                return;
            }

            // Prepare the bulk reorder data for your new API
            const reorderData = reorderedLots.map((lot, index) => ({
                lot_id: lot._id,
                old_lot_number: lot.lot_number,
                new_lot_number: index + 1
            }));

            console.log('Sending reorder data:', reorderData);

            // Call your new bulk reorder API
            const response = await dataService.reorderLots({
                auction_id: auctionData.auction_id,
                lot_orders: reorderData
            });

            if (response.error === false) {
                onReorderSuccess('Lots successfully reordered for the auction');
                onClose();
            } else {
                onReorderError(response.message || 'There was an error reordering the lots for the auction.');
            }
        } catch (error) {
            console.error('Reorder error:', error);
            onReorderError('There was an error reordering the lots for the auction.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            setShowDiscardDialog(true);
        } else {
            onClose();
        }
    };

    const handleDiscardChanges = () => {
        setShowDiscardDialog(false);
        setHasChanges(false);
        // Clear lists so next open does not flash the discarded order
        setReorderedLots([]);
        setDndLots([]);
        onClose();
    };

    const handleKeepEditing = () => {
        setShowDiscardDialog(false);
        setShowSaveDialog(false);
    };

    const confirmSave = () => {
        setShowSaveDialog(false);
        handleSave();
    };

    const handleSaveClick = () => {
        if (hasChanges) {
            setShowSaveDialog(true);
        } else {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog as="div" open={isOpen} onClose={() => handleClose(false)}
            data-te-modal-init
            className="fixed left-0 top-0 z-[1055] h-full w-full overflow-y-auto overflow-x-hidden outline-none bg-[#292929cc]"
            id="lot-reorder-modal"
            tabIndex="-1"
            aria-labelledby="lot-reorder-modal-label"
            aria-modal="true"
            role="dialog"
        >
            <Dialog.Panel
                data-te-modal-dialog-ref
                className="relative flex min-h-[calc(100%-1rem)]
                    items-center mx-3 transition-all duration-300 ease-in-out "
            >
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            handleClose();
                        }
                    }}
                >
                    <div 
                        className="bg-white rounded-lg shadow-lg max-w-6xl w-full 
                        mx-4 max-h-[90vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 flex-none">
                            <div className="text-center flex-1">
                                <h2 className="text-xl font-semibold text-gray-900">Reorder your lots</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    To re-order the lots, drag and drop the lots in place. Once done, click Save.
                                </p>
                            </div>
                            <button
                                onClick={handleClose}
                                type="button" 
                                data-testid="close-reorder-modal"
                                disabled={isLoading}
                                className="box-content rounded-none border-none"
                            >
                                <img src="/images/CloseButton-modalcontent.svg" alt="img" />
                            </button>
                        </div>
                        {/* Grid Container */}
                        <div className="bg-gray-50 m-6 flex-1 min-h-0 overflow-y-auto border-rounded customScrollbar">
                            {isLoadingLots ? (
                                <ul
                                    className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4"
                                    style={{ minHeight: '400px', listStyle: 'none' }}
                                >
                                    {Array.from({ length: 8 }).map((_, index) => (
                                        <li key={index} className="mb-4">
                                            <div
                                                className="
                                                    bg-white
                                                    border border-gray-200
                                                    rounded-lg
                                                    p-4
                                                    flex
                                                    flex-col
                                                    overflow-hidden
                                                    h-64
                                                "
                                            >
                                                <div className="relative flex-1 min-h-0 overflow-hidden rounded">
                                                    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
                                                        <Skeleton height="100%" className="w-full" />
                                                    </SkeletonTheme>
                                                </div>
                                                <div className="p-2 bg-white flex-shrink-0 h-12 flex items-center">
                                                    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
                                                        <Skeleton height={16} width="80%" />
                                                    </SkeletonTheme>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <ul
                                    ref={listRef}
                                    className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4"
                                    style={{ minHeight: '400px', listStyle: 'none' }}
                                >
                                    {reorderedLots.map((lot, index) => (
                                        <li key={lot._id} className="mb-4" onPointerDown={handleItemPointerDown}>
                                            <div
                                                className="
                                                    bg-white
                                                    border border-gray-200
                                                    rounded-lg
                                                    p-4
                                                    cursor-move
                                                    hover:shadow-md
                                                    transition-shadow
                                                    flex
                                                    flex-col
                                                    overflow-hidden
                                                    h-64
                                                "
                                            >
                                                <div className="relative flex-1 min-h-0 overflow-hidden rounded bg-gray-100">
                                                    <img
                                                        src={
                                                            lot?.images && lot.images.length > 0
                                                                ? `${config.Storage.CDN}${
                                                                    lot.images.find(img => img.featured)?.url ||
                                                                    lot.images[0].url
                                                                }`
                                                                : "/images/img-lotlist.png"
                                                        }
                                                        alt={lot.title1}
                                                        className="absolute inset-0 w-full h-full object-contain"
                                                    />
                                                </div>
                                                <div className="p-2 bg-white flex-shrink-0 h-12 flex items-center">
                                                    <p className="text-xs text-gray-900 font-medium truncate" title={lot.title1}>
                                                        {index + 1}. {
                                                            lot.title1.length > 25
                                                                ? lot.title1.substring(0, 25) + '...'
                                                                : lot.title1
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-center p-6 flex-none">
                            <button
                                onClick={handleSaveClick}
                                disabled={!hasChanges || isLoading}
                                className={`px-8 py-2 rounded-lg font-medium ${
                                    hasChanges && !isLoading
                                        ? 'bg-black text-white hover:bg-gray-800'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                } transition-colors`}
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>

                    {/* Save Confirmation Dialog */}
                    {showSaveDialog && (
                        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Save changes?</h3>
                                <p className="text-gray-600 mb-6">Your lot order will be updated.</p>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={handleKeepEditing}
                                        className="flex-1 px-4 py-2 border border-gray-300 
                                            rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Keep editing
                                    </button>
                                    <button
                                        onClick={confirmSave}
                                        className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Discard Confirmation Dialog */}
                    {showDiscardDialog && (
                        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Are you sure you want to discard your changes?
                                </h3>
                                <p className="text-gray-600 mb-6">You will lose all changes made to the lot order.</p>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={handleKeepEditing}
                                        className="flex-1 px-4 py-2 border border-gray-300 
                                            rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer"
                                    >
                                        Keep editing
                                    </button>
                                    <button
                                        onClick={handleDiscardChanges}
                                        className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                                    >
                                        Discard
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}